import {
  isAddedSource,
  kitAssetSourceOf,
  type KitAssetSource,
  type SoundCheck,
} from 'fym-shared/src/music24/addedAssets';
import type { SerializedKitAsset } from '../../../../redux/api/fymAPI.types';

/** The source of a kit asset version; an older answer has none, so it is `made`. Pure. */
export function sourceOfAsset(asset: Pick<SerializedKitAsset, 'source'> | null | undefined): KitAssetSource {
  return kitAssetSourceOf(asset?.source);
}

/** True for a version that the artist added (an upload or a YouTube link): `origin`, else the source. Pure. */
export function isAddedAsset(asset: Pick<SerializedKitAsset, 'source' | 'origin'> | null | undefined): boolean {
  if (asset?.origin !== undefined) {
    return asset.origin === 'added';
  }
  return isAddedSource(sourceOfAsset(asset));
}

/** The sound check of an uploaded music video: the top-level field, else the stored params. Pure. */
export function soundCheckOfAsset(asset: Pick<SerializedKitAsset, 'soundCheck' | 'params'>): SoundCheck | null {
  return asset.soundCheck ?? asset.params.soundCheck ?? null;
}

/**
 * True when the sound check says the sound is not the song (D4: a warning, never a block). A match
 * shows nothing; an unknown verdict (also with no master) shows nothing. Pure.
 */
export function isSoundMismatch(check: SoundCheck | null | undefined): boolean {
  return check?.verdict === 'mismatch';
}

/** "3:54". Null for an unknown length. Pure. */
export function formatClock(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds < 0) {
    return null;
  }
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest < 10 ? '0' : ''}${rest}`;
}

/** "4K", "1080p", "720p" from the height of a video, else null. Pure. */
export function videoSizeLabel(height: number | null | undefined): string | null {
  if (height === null || height === undefined || height <= 0) {
    return null;
  }
  if (height >= 2160) {
    return '4K';
  }
  return `${height >= 1080 ? 1080 : height}p`;
}

/** "2.9 GB", "640 MB". Pure. */
export function formatBytes(bytes: number): string {
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) {
    return `${gb.toFixed(1)} GB`;
  }
  const mb = bytes / 1024 ** 2;
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}
